import React, {useState} from 'react';
import {StyleSheet, View} from 'react-native';
import {useMutation, useQueryClient} from '@tanstack/react-query';
import {useTranslation} from 'react-i18next';

import {invalidateScheduleDependent} from '@/entities/attendance/cache';
import {applyDays, type DayChoice} from '@/entities/calendar/apply';
import {expandRange, MAX_RANGE_DAYS} from '@/entities/calendar/dates';
import type {MonthCell} from '@/entities/calendar/month';
import type {CalendarException} from '@/entities/calendar/types';
import {formatDayMinutes} from '@/shared/lib/format';
import {messageOf} from '@/shared/lib/errors';
import {Banner} from '@/shared/ui/banner';
import {AppButton} from '@/shared/ui/button';
import {DateField} from '@/shared/ui/date-field';
import {AppInput} from '@/shared/ui/input';
import {ChoiceChips} from '@/shared/ui/segmented';
import {Sheet} from '@/shared/ui/sheet';
import {AppText} from '@/shared/ui/text';
import {toast} from '@/shared/ui/toast';
import {Toggle} from '@/shared/ui/toggle';
import {spacing} from '@/shared/ui/theme';

/**
 * Один день календаря графика — или несколько подряд.
 *
 * Выбор из четырёх: «Как по графику» (своей записи на дате нет — день решает недельный
 * шаблон), «Праздник», «Выходной», «Рабочий день» (перенос). Пишется запись ЭТОГО
 * уровня — компании или выбранного отдела; запись, пришедшая сверху, отсюда не
 * удаляется, а перекрывается своей: так её и видит отчёт.
 */
type Choice = DayChoice;

const styles = StyleSheet.create({
  form: {gap: spacing(4)},
  actions: {gap: spacing(2)},
});

export function DaySheet({
  companyId,
  departmentId,
  cell,
  exceptions,
  visible,
  onClose,
}: {
  companyId: string;
  /** sv_control `department_id`; нет — календарь компании. */
  departmentId?: string;
  cell: MonthCell;
  /** Записи, видимые в месяце: по ним находятся свои записи диапазона для удаления. */
  exceptions: ReadonlyArray<CalendarException>;
  visible: boolean;
  onClose: () => void;
}) {
  const {t} = useTranslation();
  const qc = useQueryClient();
  const ownHere = cell.exception && !cell.inherited ? cell.exception : null;

  const [choice, setChoice] = useState<Choice>(ownHere ? ownHere.kind : 'schedule');
  const [title, setTitle] = useState(ownHere?.title ?? '');
  const [several, setSeveral] = useState(false);
  const [until, setUntil] = useState(cell.date);

  const dates = several ? expandRange(cell.date, until) : [cell.date];

  const save = useMutation({
    mutationFn: () => applyDays(companyId, departmentId, dates, choice, title, exceptions),
    onSuccess: async () => {
      await Promise.all([
        qc.invalidateQueries({queryKey: ['calendar', companyId]}),
        invalidateScheduleDependent(qc),
      ]);
      onClose();
      toast.success(t('schedule.cal.saved', {count: dates.length}));
    },
  });

  // Что стоит на дате сейчас — одной фразой.
  const now = (() => {
    const time =
      cell.start != null && cell.end != null
        ? `${formatDayMinutes(cell.start)}–${formatDayMinutes(cell.end)}`
        : null;
    const what = t(`schedule.cal.kind.${cell.kind}`);
    const parts = [what, time, cell.exception?.title || null].filter(Boolean).join(' · ');
    return cell.inherited
      ? t('schedule.cal.nowInherited', {what: parts})
      : t('schedule.cal.now', {what: parts});
  })();

  const options: ReadonlyArray<{value: Choice; label: string}> = [
    // Над днём уже есть запись сверху: без своей записи действует она, а не неделя.
    {
      value: 'schedule',
      label: t(cell.inherited ? 'schedule.cal.choice.above' : 'schedule.cal.choice.schedule'),
    },
    {value: 'holiday', label: t('calendar.kind.holiday')},
    {value: 'day_off', label: t('calendar.kind.day_off')},
    {value: 'workday', label: t('calendar.kind.workday')},
  ];

  const [y, m, d] = cell.date.split('-');

  return (
    <Sheet
      visible={visible}
      onClose={save.isPending ? () => {} : onClose}
      title={`${d}.${m}.${y} · ${t(`home.person.weekday.${cell.weekday}`)}`}
      subtitle={now}
      testID="day-sheet">
      <View style={styles.form}>
        {cell.inherited ? (
          <Banner tone="info" title={t('schedule.cal.inheritedHint')} />
        ) : null}

        <ChoiceChips options={options} value={choice} onChange={setChoice} testID="day-choice" />
        <AppText variant="caption">
          {choice === 'schedule'
            ? t(cell.inherited ? 'schedule.cal.choiceHint.above' : 'schedule.cal.choiceHint.schedule')
            : t(`calendar.kindHint.${choice}`)}
        </AppText>

        {choice !== 'schedule' ? (
          <AppInput
            label={t('calendar.titleLabel')}
            value={title}
            onChangeText={setTitle}
            maxLength={200}
          />
        ) : null}

        <Toggle
          label={t('calendar.several')}
          hint={t('calendar.severalHint', {max: MAX_RANGE_DAYS})}
          value={several}
          onChange={setSeveral}
        />
        {several ? (
          <DateField label={t('calendar.dateTo')} value={until} onChange={setUntil} />
        ) : null}

        {save.isError ? (
          <Banner tone="error" title={messageOf(save.error, t('errors.unknown'))} />
        ) : null}

        <View style={styles.actions}>
          <AppButton
            title={
              dates.length > 1
                ? t('calendar.saveMany', {count: dates.length})
                : t('actions.save')
            }
            onPress={() => save.mutate()}
            loading={save.isPending}
            disabled={save.isPending}
            testID="day-save"
          />
          <AppButton
            title={t('actions.cancel')}
            tone="outline"
            onPress={onClose}
            disabled={save.isPending}
          />
        </View>
      </View>
    </Sheet>
  );
}
